/**
 * ScanBlocks - vue dashboard a DEUX blocs : Microsoft 365 et Microsoft Azure.
 *
 * Chaque bloc :
 *   - logo officiel (BrandIcon) + titre
 *   - toggle local (Switch) pour activer/desactiver l affichage du bloc
 *   - bouton de lancement du scan (un par bloc)
 *   - resume du dernier scan : score /100, compteurs par statut, date
 *   - barre de progression live pendant un scan (poll auto)
 *
 * APIs reutilisees :
 *   - M365  : onboardingApi.status() + scanApi (trigger/list)
 *   - Azure : azureConnectionsApi.list() + azureScansApi (trigger/latest)
 *
 * Le toggle est purement visuel (localStorage via useScanVisibility).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Switch, Badge, Caption1, Body1, Title3, Spinner,
  tokens, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ArrowSync20Regular, Play20Regular, Open16Regular } from '@fluentui/react-icons';
import { BrandIcon } from './BrandIcon';
import { useScanVisibility } from '../hooks/useScanVisibility';
import { scanApi, type ScanDoc } from '../api/scanApi';
import { onboardingApi } from '../api/onboardingApi';
import { azureConnectionsApi, type AzureConnection } from '../api/azureConnectionsApi';
import { azureScansApi, type AzureScan } from '../api/azureScansApi';
import { scanQuotaApi, type ScanQuotaScope } from '../api/scanQuotaApi';

// ---------------------------------------------------------------------------
// Helpers communs
// ---------------------------------------------------------------------------
function scoreColor(score: number | null): string {
  if (score == null) return tokens.colorNeutralForeground3;
  if (score >= 80) return '#1F7A45';
  if (score >= 60) return '#A86317';
  return '#B82329';
}

const COUNT_COLORS = {
  pass:    '#1F7A45',
  warning: '#A86317',
  fail:    '#B82329',
  manual:  '#4A4A6E',
  error:   '#7A1F23',
} as const;

/** Bandeau "connecté" : pastille verte + identité du tenant / de la souscription
 *  + action (bouton de scan) alignée à droite dans le cadre. */
function ConnectedLine({ title, primary, secondary, fullId, action }: { title: string; primary: string; secondary?: string; fullId?: string; action?: React.ReactNode }) {
  return (
    <div
      title={fullId}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, marginTop: 4,
        padding: '10px 14px', borderRadius: 8, background: '#F1F6FB', border: '1px solid #D6E2F0',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#1F7A45', flexShrink: 0, boxShadow: '0 0 0 2px #DCF0E5' }} aria-hidden />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0, flex: 1 }}>
        <Caption1 style={{ color: '#3A5470', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>{title}</Caption1>
        <Body1 style={{ fontWeight: 700, color: '#142939', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary}</Body1>
        {secondary && (
          <Caption1 style={{ color: '#3A5470', fontFamily: 'Consolas, monospace', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{secondary}</Caption1>
        )}
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}

const TIER_LABEL: Record<string, string> = {
  free: 'Gratuit', starter: 'Starter', pro: 'Pro', business: 'Business', enterprise: 'Enterprise',
};

/** Note de quota sous un bloc de scan : limite du plan, restant, ou message de blocage. */
function QuotaNote({ q, onUpgrade }: { q?: ScanQuotaScope; onUpgrade: () => void }) {
  const { i18n } = useTranslation();
  if (!q) return null;

  // Plans illimités : indicateur discret toujours visible.
  if (q.unlimited) {
    return (
      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: 10 }}>
        Plan {TIER_LABEL[q.tier] ?? q.tier} · scans illimités
      </Caption1>
    );
  }

  // Azure indisponible (plan gratuit)
  if (q.azureDisabled) {
    return (
      <MessageBar intent="warning" style={{ marginTop: 12 }}>
        <MessageBarBody>
          Les scans Azure ne sont pas inclus dans le plan {TIER_LABEL[q.tier] ?? q.tier}.{' '}
          <span onClick={onUpgrade} style={{ cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>Passer à un plan supérieur</span>
        </MessageBarBody>
      </MessageBar>
    );
  }

  const period = q.windowDays === 7 ? 'semaine' : q.windowDays === 30 ? 'mois' : `${q.windowDays} j`;
  const remaining = Math.max(0, q.remaining ?? 0);
  const nextStr = q.nextAllowedAt ? new Date(q.nextAllowedAt).toLocaleDateString(i18n.language) : null;
  return (
    <div style={{ marginTop: 10 }}>
      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
        Plan {TIER_LABEL[q.tier] ?? q.tier} · {q.limit} scan / {period}
        {q.bonus > 0 ? ` · +${q.bonus} bonus` : ''} · <strong>{remaining}</strong> restant
      </Caption1>
      {!q.allowed && (
        <MessageBar intent="info" style={{ marginTop: 8 }}>
          <MessageBarBody>
            Quota atteint{nextStr ? ` — prochain scan possible le ${nextStr}` : ''}.{' '}
            <span onClick={onUpgrade} style={{ cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>Augmenter la limite</span>
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}

function ProgressLine({ current, total, label }: { current: number; total: number; label?: string }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ height: 8, borderRadius: 4, background: tokens.colorNeutralBackground3, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: `linear-gradient(90deg, ${tokens.colorBrandBackground} 0%, ${tokens.colorBrandStroke1} 100%)`,
          transition: 'width 0.4s ease',
        }} />
      </div>
      <Caption1 style={{ color: tokens.colorNeutralForeground3, marginTop: 6, display: 'block', fontFamily: 'Consolas, monospace', fontSize: 11 }}>
        {current} / {total} ({pct}%){label ? ` · ${label}` : ''}
      </Caption1>
    </div>
  );
}

function CountChips({ counts }: { counts: { pass: number; warning: number; fail: number; manual: number; error: number } }) {
  const { t } = useTranslation();
  const items: Array<[keyof typeof COUNT_COLORS, number, string]> = [
    ['pass',    counts.pass,    t('dashboard.stats.compliant')],
    ['warning', counts.warning, t('dashboard.stats.improve')],
    ['fail',    counts.fail,    t('dashboard.stats.nonCompliant')],
    ['manual',  counts.manual,  t('dashboard.stats.manual')],
    ['error',   counts.error,   t('dashboard.stats.error')],
  ];
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12 }}>
      {items.map(([key, val, label]) => (
        <div key={key} style={{ textAlign: 'center', minWidth: 52 }}>
          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: COUNT_COLORS[key], fontVariantNumeric: 'tabular-nums' }}>{val}</div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</Caption1>
        </div>
      ))}
    </div>
  );
}

function ScoreBadge({ score }: { score: number | null }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1, color: scoreColor(score), fontVariantNumeric: 'tabular-nums' }}>
        {score == null ? '—' : score}
        <span style={{ fontSize: 16, color: tokens.colorNeutralForeground3, fontWeight: 400 }}>/100</span>
      </div>
    </div>
  );
}

/** Coque commune : header logo + titre + switch, contenu en children. */
function BlockShell({
  name, title, subtitle, enabled, onToggle, action, children,
}: {
  name: 'm365' | 'azure';
  title: string;
  subtitle?: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const accent = name === 'm365' ? '#F25022' : '#0089D6';
  return (
    <Card style={{ padding: 20, borderTop: `3px solid ${accent}`, opacity: enabled ? 1 : 0.7, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <BrandIcon name={name} size={28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Title3 style={{ display: 'block' }}>{title}</Title3>
          {subtitle && (
            <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subtitle}
            </Caption1>
          )}
        </div>
        <Switch
          checked={enabled}
          onChange={(_, d) => onToggle(d.checked)}
          label={enabled ? t('scanBlocks.enabled', 'Activé') : t('scanBlocks.disabled', 'Désactivé')}
          labelPosition="before"
        />
      </div>

      {enabled && (
        <>
          {action && <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>{action}</div>}
          {children}
        </>
      )}
    </Card>
  );
}

// ===========================================================================
//  BLOC MICROSOFT 365
// ===========================================================================
function M365Block() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { m365, setM365 } = useScanVisibility();
  const [triggering, setTriggering] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onboardingQ = useQuery({
    queryKey: ['onboarding-status'],
    queryFn:  () => onboardingApi.status(),
    staleTime: 60_000, retry: false,
  });

  const connected = onboardingQ.data?.connected === true;

  const scansQ = useQuery({
    queryKey: ['scans-self'],
    queryFn:  () => scanApi.list(10),
    enabled:  connected && m365,
    staleTime: 10_000, retry: false,
    refetchInterval: (q) => {
      const items = (q.state.data as { items?: ScanDoc[] } | undefined)?.items ?? [];
      return items.some(s => s.status === 'queued' || s.status === 'running') ? 5_000 : false;
    },
  });

  const items   = scansQ.data?.items ?? [];
  const latest  = items[0];
  const running = items.find(s => s.status === 'queued' || s.status === 'running');

  const quotaQ = useQuery({ queryKey: ['scan-quota'], queryFn: () => scanQuotaApi.get(), staleTime: 30_000, retry: false });
  const mq = quotaQ.data?.m365;
  const quotaBlocked = !!(mq && !mq.unlimited && !mq.allowed);

  async function onTrigger() {
    setTriggering(true); setErr(null);
    try { await scanApi.trigger('full'); await scansQ.refetch(); await quotaQ.refetch(); }
    catch (e) { setErr((e as Error)?.message ?? t('liveScanPanel.error.title')); }
    finally { setTriggering(false); }
  }

  const subtitle = onboardingQ.data?.organizationName ?? onboardingQ.data?.defaultDomain ?? undefined;

  const launchBtn = connected ? (
    <Button
      appearance="primary"
      icon={(triggering || running) ? <Spinner size="tiny" /> : <Play20Regular />}
      onClick={onTrigger}
      disabled={triggering || !!running || quotaBlocked}
    >
      {running ? t('liveScanPanel.scanning') : t('nisAzure.scanNow', 'Scanner maintenant')}
    </Button>
  ) : undefined;

  return (
    <BlockShell name="m365" title="Microsoft 365" subtitle={subtitle} enabled={m365} onToggle={setM365}>
      {/* Tenant M365 connecté (cadre + bouton de scan à droite) */}
      {connected && (
        <ConnectedLine
          title={t('scanBlocks.connectedTenant', 'Tenant connecté')}
          primary={onboardingQ.data?.organizationName ?? t('scanBlocks.unknownTenant', 'Tenant inconnu')}
          secondary={onboardingQ.data?.onmsDomain ?? onboardingQ.data?.defaultDomain ?? (onboardingQ.data?.tenantId ? `${onboardingQ.data.tenantId.substring(0, 8)}…` : undefined)}
          fullId={onboardingQ.data?.tenantId ?? undefined}
          action={launchBtn}
        />
      )}

      {err && <MessageBar intent="error" style={{ marginTop: 12 }}><MessageBarBody>{err}</MessageBarBody></MessageBar>}

      {/* Quota de scans selon le plan */}
      {connected && <QuotaNote q={mq} onUpgrade={() => navigate('/billing')} />}

      {/* Pas connecte */}
      {!connected && (
        <div style={{ marginTop: 16 }}>
          <Body1 block style={{ color: tokens.colorNeutralForeground2 }}>
            {t('scanBlocks.m365.notConnected', 'Tenant Microsoft 365 non connecté.')}
          </Body1>
          <Button appearance="secondary" style={{ marginTop: 10 }} onClick={() => navigate('/onboarding')}>
            {t('scanBlocks.connect', 'Connecter')}
          </Button>
        </div>
      )}

      {/* En cours */}
      {connected && running && (
        <ProgressLine
          current={running.progress?.current ?? 0}
          total={running.progress?.total ?? 25}
          label={running.progress?.currentControl}
        />
      )}

      {/* Termine */}
      {connected && !running && latest && latest.status === 'done' && (
        <M365Summary scan={latest} lang={i18n.language} />
      )}

      {/* Erreur */}
      {connected && !running && latest && latest.status === 'error' && (
        <MessageBar intent="error" style={{ marginTop: 12 }}>
          <MessageBarBody>{latest.errorMessage ?? t('errors.generic')}</MessageBarBody>
        </MessageBar>
      )}

      {/* Connecte mais jamais scanne */}
      {connected && !running && !latest && (
        <Body1 block style={{ marginTop: 16, color: tokens.colorNeutralForeground2 }}>
          {t('liveScanPanel.subtitleNoScan')}
        </Body1>
      )}

      {/* Lien vers la page detail M365 (contrôles) */}
      {connected && (
        <Button
          appearance="transparent" size="small" icon={<Open16Regular />}
          style={{ marginTop: 12, paddingLeft: 0 }}
          onClick={() => navigate('/controls')}
        >
          {t('scanBlocks.m365.detail', 'Voir le détail M365')}
        </Button>
      )}
    </BlockShell>
  );
}

function M365Summary({ scan, lang }: { scan: ScanDoc; lang: string }) {
  const { t } = useTranslation();
  const r = scan.results ?? [];
  const counts = {
    pass:    r.filter(x => x.Status === 'pass').length,
    warning: r.filter(x => x.Status === 'warning' || x.Status === 'warn').length,
    fail:    r.filter(x => x.Status === 'fail').length,
    manual:  r.filter(x => x.Status === 'manual' || x.Status === 'skipped').length,
    error:   r.filter(x => x.Status === 'error').length,
  };
  const score = typeof scan.globalScore === 'number' ? scan.globalScore : null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {t('liveScanPanel.completedAt')}<br />
          <span style={{ color: tokens.colorNeutralForeground2 }}>
            {new Date(scan.finishedAt ?? scan.startedAt).toLocaleString(lang)}
          </span>
        </Caption1>
        <ScoreBadge score={score} />
      </div>
      <CountChips counts={counts} />
    </div>
  );
}

// ===========================================================================
//  BLOC MICROSOFT AZURE
// ===========================================================================
function AzureBlock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { azure, setAzure } = useScanVisibility();
  const [triggering, setTriggering] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const connQ = useQuery({
    queryKey: ['azure-connections'],
    queryFn:  () => azureConnectionsApi.list(),
    staleTime: 60_000, retry: false,
  });

  const connections: AzureConnection[] = connQ.data?.connections ?? [];
  const conn = connections[0];

  const scanQ = useQuery({
    queryKey: ['azure-latest', conn?.id],
    queryFn:  () => azureScansApi.latest(conn!.id),
    enabled:  !!conn && azure,
    staleTime: 10_000, retry: false,
    refetchInterval: (q) => {
      const s = q.state.data as AzureScan | null | undefined;
      return (s?.status === 'queued' || s?.status === 'running') ? 4_000 : false;
    },
  });

  const scan = scanQ.data ?? null;
  const running = scan?.status === 'queued' || scan?.status === 'running';

  const quotaQ = useQuery({ queryKey: ['scan-quota'], queryFn: () => scanQuotaApi.get(), staleTime: 30_000, retry: false });
  const aq = quotaQ.data?.azure;
  const azureBlocked = !!(aq && !aq.unlimited && (!aq.allowed || aq.azureDisabled));

  async function onTrigger() {
    if (!conn) return;
    setTriggering(true); setErr(null);
    try { await azureScansApi.trigger(conn.id); await scanQ.refetch(); await quotaQ.refetch(); }
    catch (e) { setErr((e as Error)?.message ?? 'Erreur'); }
    finally { setTriggering(false); }
  }

  const launchBtn = conn ? (
    <Button
      appearance="primary"
      icon={(triggering || running) ? <Spinner size="tiny" /> : <Play20Regular />}
      onClick={onTrigger}
      disabled={triggering || running || azureBlocked}
    >
      {running ? t('liveScanPanel.scanning') : t('nisAzure.scanNow')}
    </Button>
  ) : undefined;

  return (
    <BlockShell
      name="azure"
      title="Microsoft Azure"
      subtitle={conn ? conn.name : undefined}
      enabled={azure}
      onToggle={setAzure}
    >
      {/* Souscription Azure connectée (cadre + bouton de scan à droite) */}
      {conn && (
        <ConnectedLine
          title={t('scanBlocks.connectedSub', 'Souscription Azure connectée')}
          primary={conn.name}
          secondary={conn.subscriptionId ? `Sub ID : ${conn.subscriptionId}` : undefined}
          fullId={conn.tenantId ? `Tenant : ${conn.tenantId}` : undefined}
          action={launchBtn}
        />
      )}

      {err && <MessageBar intent="error" style={{ marginTop: 12 }}><MessageBarBody>{err}</MessageBarBody></MessageBar>}

      {/* Quota / disponibilité Azure selon le plan */}
      <QuotaNote q={aq} onUpgrade={() => navigate('/billing')} />

      {/* Pas de connexion Azure */}
      {!conn && (
        <div style={{ marginTop: 16 }}>
          <Body1 block style={{ color: tokens.colorNeutralForeground2 }}>
            {t('nisAzure.empty.body')}
          </Body1>
          <Button appearance="secondary" style={{ marginTop: 10 }} onClick={() => navigate('/tenants')}>
            {t('nisAzure.empty.cta')}
          </Button>
        </div>
      )}

      {/* En cours */}
      {conn && running && (
        <ProgressLine
          current={scan?.progress?.current ?? 0}
          total={scan?.progress?.total ?? 40}
          label={scan?.progress?.currentControl}
        />
      )}

      {/* Termine */}
      {conn && !running && scan && scan.status === 'done' && (
        <AzureSummary scan={scan} />
      )}

      {/* Erreur */}
      {conn && !running && scan && scan.status === 'error' && (
        <MessageBar intent="error" style={{ marginTop: 12 }}>
          <MessageBarBody>{scan.errorMessage ?? t('errors.generic')}</MessageBarBody>
        </MessageBar>
      )}

      {/* Connecte mais jamais scanne */}
      {conn && !running && !scan && (
        <Body1 block style={{ marginTop: 16, color: tokens.colorNeutralForeground2 }}>
          {t('nisAzure.score.noScan')}
        </Body1>
      )}

      {/* Lien vers la page detail Azure */}
      {conn && (
        <Button
          appearance="transparent" size="small" icon={<Open16Regular />}
          style={{ marginTop: 12, paddingLeft: 0 }}
          onClick={() => navigate('/azure')}
        >
          {t('scanBlocks.azure.detail', 'Voir le détail Azure')}
        </Button>
      )}
    </BlockShell>
  );
}

function AzureSummary({ scan }: { scan: AzureScan }) {
  const r = scan.results ?? [];
  const counts = {
    pass:    r.filter(x => x.status === 'pass').length,
    warning: r.filter(x => x.status === 'warning').length,
    fail:    r.filter(x => x.status === 'fail').length,
    manual:  r.filter(x => x.status === 'manual' || x.status === 'not_applicable').length,
    error:   r.filter(x => x.status === 'error').length,
  };
  const { t, i18n } = useTranslation();
  const score = typeof scan.globalScore === 'number' && scan.results.length > 0 ? scan.globalScore : null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {t('liveScanPanel.completedAt')}<br />
          <span style={{ color: tokens.colorNeutralForeground2 }}>
            {new Date(scan.finishedAt ?? scan.startedAt).toLocaleString(i18n.language)}
          </span>
        </Caption1>
        <ScoreBadge score={score} />
      </div>
      <CountChips counts={counts} />
    </div>
  );
}

// ===========================================================================
//  CONTENEUR : 2 blocs cote a cote (responsive)
// ===========================================================================
export function ScanBlocks() {
  const { t } = useTranslation();
  const { m365, azure } = useScanVisibility();

  // Si les deux sont desactives, on garde quand meme les coques (avec leur
  // switch) pour pouvoir reactiver — gere dans chaque bloc.
  const bothOff = !m365 && !azure;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: 16,
        alignItems: 'stretch',
      }}>
        <M365Block />
        <AzureBlock />
      </div>
      {bothOff && (
        <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: 10, textAlign: 'center' }}>
          {t('scanBlocks.allDisabled', 'Les deux périmètres sont masqués. Réactive un bloc avec son interrupteur.')}
        </Caption1>
      )}
    </div>
  );
}
